/**
 * Addi (Yuno BNPL) document-type validation.
 *
 * Addi only accepts Cédula de Ciudadanía (`CC`) as the buyer's identity
 * document. Any other document type (`NIT`, `CE`, `TI`, `RC`, …) is declined
 * when `createPayment` runs, so the payment fails at click time in the funnel
 * (no redirect to Addi happens). This module centralizes the fixed rule so the
 * checkout paths can disable the pay button and surface a warning before the
 * buyer attempts a payment that would deterministically fail.
 */

/**
 * Document type brand values Addi accepts. Fixed rule (not whitelabel-driven):
 * Addi only supports Cédula de Ciudadanía.
 * @type {string[]}
 */
export const ADDI_ALLOWED_DOCUMENT_TYPES = ['CC'];

/**
 * Whether the selected payment method is Addi. Addi is delivered through the
 * Yuno gateway as a BNPL method, identified by provider `yuno` + engine `addi`.
 * @param {object} [selectedPaymentMethod] - The selected payment method.
 * @returns {boolean} True when the method is Addi.
 */
export const isAddiPaymentMethod = (selectedPaymentMethod) =>
  selectedPaymentMethod?.provider === 'yuno' && selectedPaymentMethod?.engine === 'addi';

/**
 * Whether paying with Addi must be blocked because the buyer chose a document
 * type Addi does not accept.
 *
 * Returns `false` when Addi is not the selected method, and `false` when no
 * document type is chosen yet (nothing to warn about — the usual required-field
 * validation handles the empty case). Returns `true` only for a present
 * document type that is not in {@link ADDI_ALLOWED_DOCUMENT_TYPES}.
 *
 * @param {object} params - Parameters.
 * @param {object} [params.selectedPaymentMethod] - The selected payment method.
 * @param {string} [params.purchaserDocumentType] - The buyer's document type brand value (from the `purchaser` form).
 * @returns {boolean} True when the Addi payment must be blocked.
 */
export const isAddiBlockedByDocument = ({ selectedPaymentMethod, purchaserDocumentType }) => {
  if (!isAddiPaymentMethod(selectedPaymentMethod)) return false;
  if (!purchaserDocumentType) return false;
  return !ADDI_ALLOWED_DOCUMENT_TYPES.includes(purchaserDocumentType);
};
